// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CombinedState, StatesOrdering, Workspace } from 'reducers';
import ObjectItemContainer from 'containers/reference-annotation-page/standard-workspace/objects-side-bar/object-item';
import { setGroupState } from 'actions/group-actions';

import { groupObjects } from 'utils/group';
import { Col, Row } from 'antd';
import {
    CaretDownOutlined, CaretRightOutlined,
} from '@ant-design/icons';

import { updateAnnotationsAsync } from 'actions/annotation-actions';
import LabelItemComponent from './label-item';
import ObjectListHeader from './objects-list-header';

interface Props {
    workspace: Workspace;
    readonly: boolean;
    statesHidden: boolean;
    statesLocked: boolean;
    statesCollapsedAll: boolean;
    statesOrdering: StatesOrdering;
    sortedStatesID: number[];
    objectStates: any[];
    switchLockAllShortcut: string;
    switchHiddenAllShortcut: string;
    showGroundTruth: boolean;
    changeStatesOrdering(value: StatesOrdering): void;
    lockAllStates(): void;
    unlockAllStates(): void;
    collapseAllStates(): void;
    expandAllStates(): void;
    hideAllStates(): void;
    showAllStates(): void;
    changeShowGroundTruth(): void;
}

function ObjectListComponent(props: Props): JSX.Element {
    const {
        readonly,
        workspace,
        statesHidden,
        statesLocked,
        statesCollapsedAll,
        statesOrdering,
        // eslint-disable-next-line
        sortedStatesID,
        objectStates,
        switchLockAllShortcut,
        switchHiddenAllShortcut,
        showGroundTruth,
        changeStatesOrdering,
        lockAllStates,
        unlockAllStates,
        collapseAllStates,
        expandAllStates,
        hideAllStates,
        showAllStates,
        changeShowGroundTruth,
    } = props;

    const dispatch = useDispatch();

    const labels = useSelector((state: CombinedState) => state.annotation.job.labels);
    const { groups, updatedObjects } = groupObjects(objectStates, labels);

    let groupStates: any;

    if (!groupStates) {
        groupStates = {};
    }

    Object.keys(groups).forEach((groupId) => {
        if (!groupStates[groupId]) {
            groupStates[groupId] = { locked: false, hidden: false };
        }
    });

    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
    const [lockedGroups, setLockedGroups] = useState<Record<string, boolean>>({});
    const [hiddenGroups, setHiddenGroups] = useState<Record<string, boolean>>({});

    const handleToggleCollapse = (groupId: string) => {
        setCollapsedGroups((prevState) => {
            const updatedState = { ...prevState, [groupId]: !prevState[groupId] };
            return updatedState;
        });
    };

    const handleToggleHide = (groupId: string): void => {
        setHiddenGroups((prevState) => {
            const currentHideState = prevState[groupId] ?? false;
            const updatedHideState = !currentHideState;

            const updatedHideObjects = groups[groupId].map((object) => {
                object.hidden = updatedHideState;
                return object;
            });

            dispatch(setGroupState(groupId, { hidden: updatedHideState }));
            dispatch(updateAnnotationsAsync(updatedHideObjects));

            return { ...prevState, [groupId]: updatedHideState };
        });
    };

    const handleToggleLock = (groupId: string): void => {
        setLockedGroups((prevState) => {
            const currentLockState = prevState[groupId] ?? false;
            const updatedLockState = !currentLockState;

            const updatedLockObjects = groups[groupId].map((object) => {
                object.lock = updatedLockState;
                return object;
            });

            dispatch(setGroupState(groupId, { locked: updatedLockState }));
            dispatch(updateAnnotationsAsync(updatedLockObjects));

            return { ...prevState, [groupId]: updatedLockState };
        });
    };

    return (
        <>
            <ObjectListHeader
                readonly={readonly}
                workspace={workspace}
                statesHidden={statesHidden}
                statesLocked={statesLocked}
                statesCollapsed={statesCollapsedAll}
                statesOrdering={statesOrdering}
                switchLockAllShortcut={switchLockAllShortcut}
                switchHiddenAllShortcut={switchHiddenAllShortcut}
                showGroundTruth={showGroundTruth}
                changeStatesOrdering={changeStatesOrdering}
                lockAllStates={lockAllStates}
                unlockAllStates={unlockAllStates}
                collapseAllStates={collapseAllStates}
                expandAllStates={expandAllStates}
                hideAllStates={hideAllStates}
                showAllStates={showAllStates}
                changeShowGroundTruth={changeShowGroundTruth}
                labels={labels}
            />
            <div className='cvat-objects-sidebar-states-list'>
                {Object.entries(groups).map(([groupId, objects]) => (
                    objects.length > 0 && (
                        groupId === '' ? (
                            objects.map((object) => (
                                <ObjectItemContainer
                                    key={object.clientID}
                                    readonly={readonly}
                                    objectStates={objects}
                                    clientID={object.clientID || 0}
                                />
                            ))
                        ) : (
                            <div key={groupId} className='cvat-objects-sidebar-state-item'>
                                <div className='group-header'>
                                    <Row align='middle' justify='space-between' className='cvat-objects-sidebar-label-item'>
                                        {objects.length > 0 ? (
                                            <Col span={2}>
                                                {collapsedGroups[groupId] ? (
                                                    <CaretRightOutlined
                                                        className='collapse-toggle'
                                                        onClick={() => handleToggleCollapse(groupId)}
                                                        aria-expanded='false'
                                                    />
                                                ) : (
                                                    <CaretDownOutlined
                                                        className='collapse-toggle'
                                                        onClick={() => handleToggleCollapse(groupId)}
                                                        aria-expanded='true'
                                                    />
                                                )}
                                            </Col>
                                        ) : null}
                                        <Col span={22}>
                                            <LabelItemComponent
                                                labelName={groupId}
                                                labelColor={objects[0]?.label.color || '#ddd'}
                                                visible={objects.length > 0 && !hiddenGroups[groupId]}
                                                statesHidden={objects.length > 0 && hiddenGroups[groupId]}
                                                statesLocked={objects.length > 0 && lockedGroups[groupId]}
                                                haveStyles={false}
                                                hideStates={() => {
                                                    if (objects.length > 0) handleToggleHide(groupId);
                                                }}
                                                showStates={() => {
                                                    if (objects.length > 0) handleToggleHide(groupId);
                                                }}
                                                lockStates={() => {
                                                    if (objects.length > 0) handleToggleLock(groupId);
                                                }}
                                                unlockStates={() => {
                                                    if (objects.length > 0) handleToggleLock(groupId);
                                                }}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                                {!collapsedGroups[groupId] && objects.length > 0 && (
                                    <div className='group-objects'>
                                        {objects.map((object) => (
                                            <ObjectItemContainer
                                                key={object.clientID}
                                                readonly={readonly}
                                                objectStates={objects}
                                                clientID={object.clientID || 0}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    )
                ))}
            </div>
        </>
    );
}

export default React.memo(ObjectListComponent);
